import * as React from 'react';
import {
  DataGridPremium,
  GridToolbar,
  GridRowsProp,
  GridColDef,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { Chip, Link } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';

import { DateTime } from 'luxon';

import { IIncidentDisplayData, IncidentStatus, ReportType } from '../../core/api/clients';
import { Preference, usePreferences } from '../../core/context/PreferencesContext';
import useGetFormMetadata from '../../core/api/hooks/common/useGetFormMetadata';

function IncidentDataGrid({ data, reportType, pageSize = 25 }: {
  data: UseQueryResult<IIncidentDisplayData[], unknown>,
  reportType: string,
  pageSize?: number,
}) {
  const { dataGridDensity, updatePreference, incidentGridColumns } = usePreferences();
  const metadata = useGetFormMetadata(ReportType.Incident);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return 'success';
      case 'Closed': return 'primary';
      default: return undefined;
    }
  };
  const getNavigationPath = () => {
    const { data: result } = metadata;
    return result?.isAdministrator ? 'edit' : 'view';
  };
  const isEmergencyDeclared = (params: GridRenderCellParams<any, any, any>): string => {
    if (params && params.value) {
      const { emergencyDeclared } = params.value;
      if (emergencyDeclared === 'Yes' || emergencyDeclared === true) {
        return 'YES';
      }
      return 'NO';
    }
    return 'UNKNOWN';
  };
  const getTitle = (params: GridRenderCellParams) => {
    let title = 'Incident';

    if (params.row.createdOn) {
      title = `Incident ${params.row.createdOn ? DateTime.fromISO(params.row.createdOn).toLocaleString(DateTime.DATETIME_SHORT) : 'N/A'}`;
    }
    if (params.row.eventType && params.row.flightNumber) {
      title = `${params.row.eventType} incident for flight ${params.row.flightNumber}`;
    }

    if (params.value) {
      title = params.value;
    }

    return title;
  };

  const columns: GridColDef[] = [
    { field: 'id' },
    {
      field: 'title',
      headerName: 'Event Title',
      renderCell: (params: GridRenderCellParams) => (
        <Link href={params.row.status === IncidentStatus.Open ? `form/${reportType}/${params.id}/${getNavigationPath()}` : `form/${reportType}/${params.id}/view`} underline="hover">
          {getTitle(params)}
        </Link>
      ),
      flex: 1.5,
    },
    { field: 'eventType', headerName: 'Event Type', flex: 1 },
    { field: 'eventType2', headerName: 'Event Type 2', flex: 1 },
    { field: 'eventType3', headerName: 'Event Type 3', flex: 1 },
    { field: 'aircraftNumber', headerName: 'AC', flex: 0.5 },
    { field: 'flightNumber', headerName: 'Flight', flex: 0.5 },
    {
      field: 'eventDate',
      type: 'date',
      headerName: 'Event Date',
      valueGetter: (value) => DateTime.fromISO(value).toJSDate(),
      renderCell: (params: GridRenderCellParams) => (
        params.value ? DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATE_SHORT) : 'N/A'
      ),
      flex: 0.75,
    },
    { field: 'primaryReport',
      headerName: 'Emergency',
      flex: 0.5,
      renderCell: (params) => (isEmergencyDeclared(params)) },
    {
      field: 'riskRating',
      headerName: 'Risk Rating',
      flex: 0.75,
    },
    {
      field: 'severityCategory',
      headerName: 'Severity Category',
      flex: 0.75,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.75,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          size="small"
          variant="outlined"
          label={params.value}
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'captainName',
      headerName: 'Captain',
      flex: 1,
      type: 'singleSelect',
      valueOptions: Array.from(new Set(data.data?.map((d: IIncidentDisplayData) => d.captainName).filter(Boolean))).map((value) => ({
        value,
        label: value,
      })),
    },
    {
      field: 'firstOfficerName',
      headerName: 'First Officer',
      flex: 1,
      type: 'singleSelect',
      valueOptions: Array.from(new Set(data.data?.map((d: IIncidentDisplayData) => d.firstOfficerName).filter(Boolean))).map((value) => ({
        value,
        label: value,
      })),
    },
    {
      field: 'flightAttendantName',
      headerName: 'Flight Attendant',
      flex: 1,
      type: 'singleSelect',
      valueOptions: Array.from(new Set(data.data?.map((d: IIncidentDisplayData) => d.flightAttendantName).filter(Boolean))).map((value) => ({
        value,
        label: value,
      })),
    },
  ];

  const [dataRows, setDataRows] = React.useState<GridRowsProp>([]);
  const [page, setPage] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize,
  });
  React.useEffect(() => {
    const areObjectsEqual = JSON.stringify(incidentGridColumns) === JSON.stringify({});
    if (!incidentGridColumns || areObjectsEqual) {
      updatePreference(Preference.IncidentGridColumns, {
        id: false,
        captainName: false,
        firstOfficerName: false,
        flightAttendantName: false,
        riskRating: false,
        severityCategory: false,
      });
    }
  }, []);
  React.useEffect(() => {
    if (data.status === 'success' && data.data !== undefined) {
      setDataRows([...data.data.sort((a, b) => {
        if (a.status !== b.status) {
          return a.status! === IncidentStatus.Open ? -1 : 1; // works only because there are 2 statuses
        }

        return DateTime.fromISO(b.eventDate!).toMillis() - DateTime.fromISO(a.eventDate!).toMillis();
      })]);
    }
    return () => {
      setDataRows([]);
    };
  }, [data]);

  return (
    <DataGridPremium
      density={dataGridDensity}
      onDensityChange={(density) => updatePreference(Preference.DataGridDensity, density)}
      autoHeight
      paginationModel={page}
      pagination
      onPaginationModelChange={(newPage) => setPage(newPage)}
      rows={dataRows}
      columnVisibilityModel={incidentGridColumns}
      onColumnVisibilityModelChange={(model) => {
        updatePreference(Preference.IncidentGridColumns, model);
      }}
      columns={columns}
      rowHeight={35}
      pageSizeOptions={[10, 25, 50]}
      slots={{
        toolbar: GridToolbar,
      }}
    />
  );
}

export default IncidentDataGrid;
